import { Link, useNavigate } from "react-router-dom";
import { useCart, money } from "../lib/cart";
import { Button } from "../components/ui/button";
import { ShoppingBag, Minus, Plus, X } from "lucide-react";
import { ProductImage } from "../components/ProductImage";
import { useCheckoutConfig } from "../lib/checkout";

export default function Cart() {
  const navigate = useNavigate();
  const { items, remove, setQty, subtotal_cents, hasResearch } = useCart();
  const config = useCheckoutConfig();
  const shipping = config ? config.shipping_cents : null;

  if (items.length === 0) {
    return (
      <div className="relative max-w-4xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={48} className="text-[#59394F]/50 mx-auto mb-6" strokeWidth={1} />
        <h1 className="font-serif text-4xl mb-3">Your cart is quiet</h1>
        <p className="text-[#5E4D5A] mb-8">Nothing in here yet. Have a look at the catalog.</p>
        <Link to="/shop" className="inline-block bg-[#59394F] text-[#F4EBDD] rounded-full px-8 py-4 hover:bg-[#432A3B] transition-colors text-sm">Browse catalog</Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Shopping cart</p>
      <h1 className="font-serif text-5xl mt-2 mb-12">Your selection</h1>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
          {items.map((it) => (
            <div key={it.key} data-testid={`cart-item-${it.key}`} className="flex gap-5 bg-white rounded-2xl border border-[#A8B59A]/20 p-5">
              <ProductImage imageId={it.image_id} variant="thumb" alt={it.name} iconSize={28} className="w-24 h-24 rounded-xl flex-shrink-0" testId={`cart-image-${it.key}`} />
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg leading-tight">{it.name}</h3>
                <p className="text-xs text-[#5E4D5A] mt-1" data-testid={`cart-strength-${it.key}`}>{[it.size, it.form].filter(Boolean).join(" · ")}</p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center bg-[#F4EBDD] rounded-full border border-[#A8B59A]/30">
                    <button data-testid={`cart-minus-${it.key}`} onClick={() => setQty(it.key, it.quantity - 1)} className="p-2 hover:bg-[#A8B59A]/10 rounded-l-full transition-colors"><Minus size={12} /></button>
                    <span className="px-3 text-sm" data-testid={`cart-qty-${it.key}`}>{it.quantity}</span>
                    <button data-testid={`cart-plus-${it.key}`} onClick={() => setQty(it.key, it.quantity + 1)} className="p-2 hover:bg-[#A8B59A]/10 rounded-r-full transition-colors"><Plus size={12} /></button>
                  </div>
                  <p className="text-sm font-medium">{money(it.price_cents * it.quantity)}</p>
                </div>
              </div>
              <button onClick={() => remove(it.key)} data-testid={`remove-${it.key}`} className="text-[#5E4D5A] hover:text-[#59394F] p-1 transition-colors self-start">
                <X size={18} />
              </button>
            </div>
          ))}
        </div>

        <aside className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6 h-fit sticky top-28">
          <h3 className="font-serif text-xl mb-5">Summary</h3>
          <div className="flex justify-between text-sm text-[#5E4D5A] mb-3">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">{money(subtotal_cents)}</span>
          </div>
          <div className="flex justify-between text-sm text-[#5E4D5A] mb-5">
            <span>Shipping (flat rate){config?.meetup_enabled && <span className="block text-xs text-[#5E4D5A]" data-testid="cart-meetup-hint">Free Local Meetup available at checkout</span>}</span>
            <span data-testid="cart-shipping">{shipping === null ? "…" : money(shipping)}</span>
          </div>
          <div className="border-t border-[#A8B59A]/20 pt-5 flex justify-between font-medium text-lg mb-6">
            <span>Total</span>
            <span data-testid="cart-total">{money(subtotal_cents + (shipping || 0))}</span>
          </div>
          <Button onClick={() => navigate("/checkout")} data-testid="cart-checkout-btn" className="w-full rounded-full bg-[#59394F] hover:bg-[#432A3B] text-[#F4EBDD] py-6 h-auto transition-colors">
            Proceed to checkout
          </Button>
          {hasResearch && <p className="text-[10px] uppercase tracking-[0.2em] text-[#5E6B51] mt-5 text-center" data-testid="cart-ruo-note">Research catalog items: for research use only</p>}
        </aside>
      </div>
    </div>
  );
}
